package br.com.vvrefeicoes.portal.feature.auth.login

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.model.AppError
import br.com.vvrefeicoes.portal.core.model.AppResult
import br.com.vvrefeicoes.portal.core.storage.KeyValueStorage
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.auth_invalid_credentials
import portalvv.shared.generated.resources.auth_login_failed
import portalvv.shared.generated.resources.auth_missing_credentials
import portalvv.shared.generated.resources.auth_network_error

class LoginController(
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope,
    private val onForgotPassword: (String) -> Unit,
    private val storage: KeyValueStorage,
) {
    // E-mail.
    var login by mutableStateOf("")
    var password by mutableStateOf("")
    var rememberMe by mutableStateOf(storage.getString(REMEMBERED_LOGIN_KEY) != null)
    var loading by mutableStateOf(false)
    var errorResource by mutableStateOf<StringResource?>(null)
    var showPasswordChangedBanner by mutableStateOf(false)

    fun submit() {
        if (loading) return
        if (login.isBlank() || password.isBlank()) {
            errorResource = Res.string.auth_missing_credentials
            return
        }
        scope.launch {
            loading = true
            errorResource = null
            try {
                val result = authRepository.login(login, password)
                if (result is AppResult.Failure) {
                    errorResource =
                        when (result.error) {
                            AppError.Unauthorized -> Res.string.auth_invalid_credentials
                            AppError.Network -> Res.string.auth_network_error
                            else -> Res.string.auth_login_failed
                        }
                } else if (rememberMe) {
                    storage.putString(REMEMBERED_LOGIN_KEY, login.trim())
                } else {
                    storage.remove(REMEMBERED_LOGIN_KEY)
                }
            } finally {
                loading = false
            }
        }
    }

    // A recuperação de senha é por e-mail, então só pré-preenche quando o usuário digitou um.
    fun forgotPassword() = onForgotPassword(if ('@' in login) login.trim() else "")

    fun dismissPasswordChangedBanner() {
        showPasswordChangedBanner = false
    }
}

@Composable
fun rememberLoginController(
    onForgotPassword: (String) -> Unit,
    showPasswordChangedBanner: Boolean = false,
): LoginController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    val storage =
        remember {
            br.com.vvrefeicoes.portal.core.storage
                .createKeyValueStorage()
        }
    val controller = remember { LoginController(appGraph.authRepository, scope, onForgotPassword, storage) }
    LaunchedEffect(Unit) {
        controller.login = storage.getString(REMEMBERED_LOGIN_KEY).orEmpty()
    }
    LaunchedEffect(showPasswordChangedBanner) {
        if (showPasswordChangedBanner) controller.showPasswordChangedBanner = true
    }
    return controller
}

private const val REMEMBERED_LOGIN_KEY = "auth.rememberedLogin"
